import React, { useState } from 'react';
import {
  Typography,
  Box,
  TextField,
  Divider,
  InputAdornment,
  Grid,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  Checkbox,
  Tooltip,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import {
  getSharedBenchmarkParameters,
  resolveSflagaEnhancements,
} from '../../utils/parameterUtils';
import { FitnessMode, EnergyAwareWeights, SflagaEnhancementOptions } from '../../types';
import {
  createComplementaryEnergyAwareWeights,
  normalizeEnergyAwareWeights,
} from '../../utils/energyAwareWeights';

interface ParameterPanelProps {
  disabled?: boolean;
  initialValues?: {
    taskCount?: number;
    qosProperties?: number;
    iterations?: number;
    initialPopulation?: number;
    selectionRate?: number;
    crossoverRate?: number;
    mutationRate?: number;
    memeplexCount?: number;
    numberOfRuns?: number;
    includeSflaGaBaseline?: boolean;
    parallelExecution?: boolean;
    heavyMode?: boolean;
    fitnessMode?: FitnessMode;
    energyAwareWeights?: EnergyAwareWeights;
    sflagaEnhancements?: SflagaEnhancementOptions;
    parameterPolicy?: string;
    seed?: number;
    taskSpecificVariationEnabled?: boolean;
  };
  onParametersChange?: (params: {
    taskCount: number;
    qosProperties: number;
    iterations: number;
    initialPopulation: number;
    selectionRate: number;
    crossoverRate: number;
    mutationRate: number;
    memeplexCount: number;
    numberOfRuns?: number;
    includeSflaGaBaseline?: boolean;
    parallelExecution?: boolean;
    heavyMode?: boolean;
    fitnessMode?: FitnessMode;
    energyAwareWeights?: EnergyAwareWeights;
    sflagaEnhancements?: SflagaEnhancementOptions;
    parameterPolicy?: string;
    seed?: number;
    taskSpecificVariationEnabled?: boolean;
  }) => void;
}

const ParameterPanel: React.FC<ParameterPanelProps> = ({
  disabled = false,
  initialValues,
  onParametersChange,
}) => {
  const HARDCODED_QOS_PROPERTY_COUNT = 9;
  const resolveVisibleSflagaEnhancements = (
    value?: SflagaEnhancementOptions
  ): SflagaEnhancementOptions => ({
    ...resolveSflagaEnhancements(value),
    worstFrogLocalSearch: true,
  });
  const clampPercentage = (value: number) => Math.max(0, Math.min(100, value));
  const enforceSharedEvolutionaryBounds = <T extends {
    selectionRate: number;
    crossoverRate: number;
    mutationRate: number;
  }>(params: T): T => ({
    ...params,
    selectionRate: clampPercentage(params.selectionRate),
    crossoverRate: clampPercentage(params.crossoverRate),
    mutationRate: clampPercentage(params.mutationRate),
  });

  // Initialize with shared benchmark parameters based on taskCount if no initialValues are provided.
  const initialTaskCount = initialValues?.taskCount ?? 10;
  const sharedDefaults = getSharedBenchmarkParameters(initialTaskCount);

  const [parameters, setParameters] = useState(() =>
    enforceSharedEvolutionaryBounds({
      taskCount: initialTaskCount,
      qosProperties: HARDCODED_QOS_PROPERTY_COUNT,
      iterations: initialValues?.iterations ?? sharedDefaults.iterations,
      initialPopulation: initialValues?.initialPopulation ?? sharedDefaults.initialPopulation,
      selectionRate: initialValues?.selectionRate ?? sharedDefaults.selectionRate,
      crossoverRate: initialValues?.crossoverRate ?? sharedDefaults.crossoverRate,
      mutationRate: Math.round(initialValues?.mutationRate ?? sharedDefaults.mutationRate),
      memeplexCount: initialValues?.memeplexCount ?? sharedDefaults.memeplexCount,
      numberOfRuns: initialValues?.numberOfRuns ?? 5, // number of runs = 5
    includeSflaGaBaseline: initialValues?.includeSflaGaBaseline ?? false,
    parallelExecution: initialValues?.parallelExecution ?? false,
    heavyMode: initialValues?.heavyMode ?? false,
    fitnessMode: initialValues?.fitnessMode ?? 'energy-aware',
    energyAwareWeights: normalizeEnergyAwareWeights(initialValues?.energyAwareWeights),
      sflagaEnhancements: resolveVisibleSflagaEnhancements(initialValues?.sflagaEnhancements),
      parameterPolicy: initialValues?.parameterPolicy ?? sharedDefaults.parameterPolicy,
      seed: initialValues?.seed,
      taskSpecificVariationEnabled: initialValues?.taskSpecificVariationEnabled ?? true,
    })
  );

  // Update parameters when initialValues change (for preset loading and complete run updates)
  React.useEffect(() => {
    if (initialValues) {
      // Use all provided values - the context already calculates shared parameters.
      // Only update if values actually changed to avoid unnecessary re-renders
      setParameters(prev => {
        // Check if taskCount changed - if so, use the shared benchmark defaults plus explicit overrides.
        if (initialValues.taskCount !== undefined && initialValues.taskCount !== prev.taskCount) {
          const sharedForTaskCount = getSharedBenchmarkParameters(initialValues.taskCount);
          return enforceSharedEvolutionaryBounds({
            taskCount: initialValues.taskCount,
            qosProperties: HARDCODED_QOS_PROPERTY_COUNT,
            iterations: initialValues.iterations ?? sharedForTaskCount.iterations,
            initialPopulation: initialValues.initialPopulation ?? sharedForTaskCount.initialPopulation,
            selectionRate: initialValues.selectionRate ?? sharedForTaskCount.selectionRate,
            crossoverRate: initialValues.crossoverRate ?? sharedForTaskCount.crossoverRate,
            mutationRate: Math.round(initialValues.mutationRate ?? sharedForTaskCount.mutationRate),
            memeplexCount: initialValues.memeplexCount ?? sharedForTaskCount.memeplexCount,
            numberOfRuns: 5, // number of runs = 5
            includeSflaGaBaseline: initialValues.includeSflaGaBaseline ?? prev.includeSflaGaBaseline,
            parallelExecution: initialValues.parallelExecution ?? prev.parallelExecution,
            heavyMode: initialValues.heavyMode ?? prev.heavyMode,
            fitnessMode: initialValues.fitnessMode ?? prev.fitnessMode,
            energyAwareWeights: normalizeEnergyAwareWeights(
              initialValues.energyAwareWeights ?? prev.energyAwareWeights
            ),
            sflagaEnhancements: resolveVisibleSflagaEnhancements(
              initialValues.sflagaEnhancements ?? prev.sflagaEnhancements
            ),
            parameterPolicy: initialValues.parameterPolicy ?? sharedForTaskCount.parameterPolicy,
            seed: initialValues.seed ?? prev.seed,
            taskSpecificVariationEnabled: initialValues.taskSpecificVariationEnabled ?? prev.taskSpecificVariationEnabled,
          });
        }
        // Otherwise, merge with existing values
        return enforceSharedEvolutionaryBounds({
          ...prev,
          ...initialValues,
          qosProperties: HARDCODED_QOS_PROPERTY_COUNT,
          // Ensure mutation rate is always rounded
          mutationRate: initialValues.mutationRate !== undefined 
            ? Math.round(initialValues.mutationRate) 
            : prev.mutationRate,
          // Preserve fitnessMode and energyAwareWeights if not provided
          includeSflaGaBaseline: initialValues.includeSflaGaBaseline ?? prev.includeSflaGaBaseline,
          parallelExecution: initialValues.parallelExecution ?? prev.parallelExecution,
          heavyMode: initialValues.heavyMode ?? prev.heavyMode,
          fitnessMode: initialValues.fitnessMode ?? prev.fitnessMode,
          energyAwareWeights: normalizeEnergyAwareWeights(
            initialValues.energyAwareWeights ?? prev.energyAwareWeights
          ),
          sflagaEnhancements: resolveVisibleSflagaEnhancements(
            initialValues.sflagaEnhancements ?? prev.sflagaEnhancements
          ),
          parameterPolicy: initialValues.parameterPolicy ?? prev.parameterPolicy,
          seed: initialValues.seed ?? prev.seed,
          taskSpecificVariationEnabled: initialValues.taskSpecificVariationEnabled ?? prev.taskSpecificVariationEnabled,
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues?.taskCount, initialValues?.iterations, initialValues?.initialPopulation, 
      initialValues?.selectionRate, initialValues?.crossoverRate, initialValues?.mutationRate,
      initialValues?.memeplexCount, initialValues?.includeSflaGaBaseline, initialValues?.parallelExecution,
      initialValues?.heavyMode, initialValues?.fitnessMode,
      initialValues?.energyAwareWeights?.fitness, initialValues?.energyAwareWeights?.energy,
      initialValues?.energyAwareWeights?.cost,
      initialValues?.sflagaEnhancements?.guidedMutation,
      initialValues?.sflagaEnhancements?.elitePreservation,
      initialValues?.sflagaEnhancements?.similarityAwareDiversification,
      initialValues?.sflagaEnhancements?.adaptiveImmigrantRefresh,
      initialValues?.parameterPolicy,
      initialValues?.seed,
      initialValues?.taskSpecificVariationEnabled]);

  const handleChange = (field: string) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    let value: any = event.target.value;
    
    // Handle numeric fields
    if (['taskCount', 'qosProperties', 'iterations', 'initialPopulation', 'selectionRate', 
         'crossoverRate', 'mutationRate', 'memeplexCount', 'numberOfRuns'].includes(field)) {
      value = parseFloat(event.target.value) || 0;
      
      // Round mutation rate to nearest integer (no decimal values)
      if (field === 'mutationRate') {
        value = Math.round(value);
      }

      if (['selectionRate', 'crossoverRate', 'mutationRate'].includes(field)) {
        value = clampPercentage(value);
      }
    }
    
    // Handle fitness mode
    if (field === 'fitnessMode') {
      value = event.target.value as FitnessMode;
    }
    
    // Handle energy-aware weights
    if (field.startsWith('energyAwareWeights.')) {
      const weightField = field.split('.')[1] as 'fitness' | 'energy';
      const weightValue = parseFloat(event.target.value);
      value = createComplementaryEnergyAwareWeights(
        weightField,
        Number.isFinite(weightValue) ? weightValue : 0
      );
      field = 'energyAwareWeights';
    }
    
    let newParams = enforceSharedEvolutionaryBounds({ ...parameters, [field]: value });
    
    // If taskCount changes, auto-adjust the shared benchmark parameters for the new problem size.
    if (field === 'taskCount') {
      const sharedForTaskCount = getSharedBenchmarkParameters(value);
      newParams = enforceSharedEvolutionaryBounds({
        ...newParams,
        ...sharedForTaskCount,
        numberOfRuns: 5, // number of runs = 5 for Statistical Analysis
        includeSflaGaBaseline: parameters.includeSflaGaBaseline,
        parallelExecution: parameters.parallelExecution,
        heavyMode: parameters.heavyMode,
        sflagaEnhancements: parameters.sflagaEnhancements,
        seed: parameters.seed,
        taskSpecificVariationEnabled: parameters.taskSpecificVariationEnabled,
      });
    }
    
    setParameters(newParams);
    if (onParametersChange) {
      onParametersChange(newParams);
    }
  };

  // Initial notification
  React.useEffect(() => {
    if (onParametersChange) {
      onParametersChange(parameters);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const wrapWithTooltip = (title: string, content: React.ReactElement, fullWidth: boolean = true) => (
    <Tooltip title={title} arrow placement="top">
      <Box sx={fullWidth ? { width: '100%' } : undefined}>
        {content}
      </Box>
    </Tooltip>
  );

  return (
    <Box
      component="fieldset"
      disabled={disabled}
      aria-disabled={disabled}
      sx={{
        border: 0,
        p: 0,
        m: 0,
        minWidth: 0,
        ...(disabled
          ? {
              opacity: 0.6,
            }
          : {}),
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, md: 3 } }}>
        <SettingsIcon sx={{ mr: 1, color: 'primary.main', fontSize: { xs: '1.25rem', md: '1.5rem' } }} />
        <Typography variant="h6" component="h2" sx={{ fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
          Parameters
        </Typography>
        <Box sx={{ ml: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
          <Tooltip title="Run the algorithms in parallel using Web Workers to reduce wall-clock time when the browser can support it.">
            <FormControlLabel
              control={
                <Checkbox
                  checked={parameters.parallelExecution}
                  onChange={(event) => {
                    const newParams = {
                      ...parameters,
                      parallelExecution: event.target.checked,
                    };
                    setParameters(newParams);
                    if (onParametersChange) {
                      onParametersChange(newParams);
                    }
                  }}
                  size="small"
                />
              }
              label="Parallel"
              sx={{ mr: 0 }}
            />
          </Tooltip>
          <Tooltip title="Enable the heavier large-scale SFLAGA refinement path. This can improve deep search at high task counts, but it increases computation cost.">
            <FormControlLabel
              control={
                <Checkbox
                  checked={parameters.heavyMode}
                  onChange={(event) => {
                    const newParams = {
                      ...parameters,
                      heavyMode: event.target.checked,
                    };
                    setParameters(newParams);
                    if (onParametersChange) {
                      onParametersChange(newParams);
                    }
                  }}
                  size="small"
                />
              }
              label="Heavy"
              sx={{ mr: 0, mt: -1 }}
            />
          </Tooltip>
        </Box>
      </Box>

      <Divider sx={{ mb: 3 }} />
      {/* Common Parameters */}
      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, fontSize: { xs: '0.875rem', md: '1rem' } }}>
        Shared Experimental Budget
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          {wrapWithTooltip(
            'Number of workflow tasks to optimize. Changing this also refreshes the shared benchmark defaults for the run.',
            <TextField
              label="Workflow Tasks"
              type="number"
              value={parameters.taskCount}
              onChange={handleChange('taskCount')}
              size="small"
              fullWidth
              inputProps={{ min: 1, max: 1500 }}
            />
          )}
        </Grid>
        <Grid item xs={12} sm={6}>
          {wrapWithTooltip(
            'Hardcoded to 9 QoS properties in the current implementation. This value is informational only and is not configurable from the UI.',
            <TextField
              label="QoS Properties"
              type="number"
              value={parameters.qosProperties}
              disabled
              size="small"
              fullWidth
              inputProps={{ min: 1, max: 20 }}
            />
          )}
        </Grid>
        <Grid item xs={12} sm={6}>
          {wrapWithTooltip(
            'Maximum number of generations or iterations each algorithm will execute for one run.',
            <TextField
              label="Iterations"
              type="number"
              value={parameters.iterations}
              onChange={handleChange('iterations')}
              size="small"
              fullWidth
              inputProps={{ min: 1, max: 10000 }}
            />
          )}
        </Grid>
        <Grid item xs={12} sm={6}>
          {wrapWithTooltip(
            'Initial population or swarm size shared across the benchmarked algorithms.',
            <TextField
              label="Initial Population"
              type="number"
              value={parameters.initialPopulation}
              onChange={handleChange('initialPopulation')}
              size="small"
              fullWidth
              inputProps={{ min: 10, max: 10000 }}
            />
          )}
        </Grid>
      </Grid>

      <Divider sx={{ mb: 3 }} />

      {/* Shared evolutionary parameters */}
      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, fontSize: { xs: '0.875rem', md: '1rem' } }}>
        Shared Evolutionary Parameters
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          {wrapWithTooltip(
            'Probability of using selection pressure instead of random parent choice in algorithms that support selection.',
            <TextField
              label="Selection Rate"
              type="number"
              value={parameters.selectionRate}
              onChange={handleChange('selectionRate')}
              size="small"
              fullWidth
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
              inputProps={{ min: 0, max: 100 }}
            />
          )}
        </Grid>
        <Grid item xs={12} sm={6}>
          {wrapWithTooltip(
            'Percentage chance of performing crossover in the algorithms that use recombination.',
            <TextField
              label="Crossover Rate"
              type="number"
              value={parameters.crossoverRate}
              onChange={handleChange('crossoverRate')}
              size="small"
              fullWidth
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
              inputProps={{ min: 0, max: 100 }}
            />
          )}
        </Grid>
        <Grid item xs={12} sm={6}>
          {wrapWithTooltip(
            'Percentage of mutation pressure used by mutation-based algorithms. Values are stored as whole numbers.',
            <TextField
              label="Mutation Rate"
              type="number"
              value={Math.round(parameters.mutationRate)}
              onChange={handleChange('mutationRate')}
              size="small"
              fullWidth
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
              inputProps={{ min: 0, max: 100, step: 1 }}
            />
          )}
        </Grid>
      </Grid>

      <Divider sx={{ mb: 3 }} />

      {/* Shared memeplex parameters */}
      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, fontSize: { xs: '0.875rem', md: '1rem' } }}>
        Shared Memeplex Parameters
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          {wrapWithTooltip(
            'Number of memeplex groups used by SFLA-family algorithms during local and shuffled search.',
            <TextField
              label="Memeplex Count"
              type="number"
              value={parameters.memeplexCount}
              onChange={handleChange('memeplexCount')}
              size="small"
              fullWidth
              inputProps={{ min: 1, max: 100 }}
            />
          )}
        </Grid>
      </Grid>

      <Divider sx={{ mb: 3 }} />

      {/* Proposed SFLAGA enhancements */}
      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, fontSize: { xs: '0.875rem', md: '1rem' } }}>
        Proposed SFLAGA Enhancements
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        These toggles affect the proposed SFLAGA variant only. The standard SFLA-GA baseline remains unchanged. Worst-frog local search is part of the default SFLAGA core and is always enabled here.
      </Typography>
      <Grid container spacing={1.5} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          <Tooltip title="Use candidate-evaluated guided mutation instead of plain random mutation when SFLAGA mutates a chromosome." arrow placement="top">
            <FormControlLabel
              control={
                <Checkbox
                  checked={parameters.sflagaEnhancements.guidedMutation}
                  onChange={(event) => {
                    const newParams = {
                      ...parameters,
                      sflagaEnhancements: {
                        ...parameters.sflagaEnhancements,
                        guidedMutation: event.target.checked,
                      },
                    };
                    setParameters(newParams);
                    if (onParametersChange) {
                      onParametersChange(newParams);
                    }
                  }}
                  size="small"
                />
              }
              label={
                <Typography sx={{ fontSize: { xs: '0.8rem', md: '0.85rem' }, color: 'success.main' }}>
                  Guided Mutation
                </Typography>
              }
            />
          </Tooltip>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Tooltip title="Protect the top SFLAGA individuals so elite solutions are not overwritten during memeplex and population updates." arrow placement="top">
            <FormControlLabel
              control={
                <Checkbox
                  checked={parameters.sflagaEnhancements.elitePreservation}
                  onChange={(event) => {
                    const newParams = {
                      ...parameters,
                      sflagaEnhancements: {
                        ...parameters.sflagaEnhancements,
                        elitePreservation: event.target.checked,
                      },
                    };
                    setParameters(newParams);
                    if (onParametersChange) {
                      onParametersChange(newParams);
                    }
                  }}
                  size="small"
                />
              }
              label={
                <Typography sx={{ fontSize: { xs: '0.8rem', md: '0.85rem' }, color: 'success.main' }}>
                  Elite Preservation
                </Typography>
              }
            />
          </Tooltip>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Tooltip title="Detect offspring that are too close to the current elite set and diversify them before acceptance. This has no practical effect if elite preservation is disabled." arrow placement="top">
            <FormControlLabel
              control={
                <Checkbox
                  checked={parameters.sflagaEnhancements.similarityAwareDiversification}
                  onChange={(event) => {
                    const newParams = {
                      ...parameters,
                      sflagaEnhancements: {
                        ...parameters.sflagaEnhancements,
                        similarityAwareDiversification: event.target.checked,
                      },
                    };
                    setParameters(newParams);
                    if (onParametersChange) {
                      onParametersChange(newParams);
                    }
                  }}
                  size="small"
                />
              }
              label={
                <Typography sx={{ fontSize: { xs: '0.8rem', md: '0.85rem' }, color: 'error.main' }}>
                  Similarity-Aware Diversification
                </Typography>
              }
            />
          </Tooltip>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Tooltip title="When SFLAGA stagnates, raise mutation pressure and inject guided immigrants into the lower tail to refresh diversity." arrow placement="top">
            <FormControlLabel
              control={
                <Checkbox
                  checked={parameters.sflagaEnhancements.adaptiveImmigrantRefresh}
                  onChange={(event) => {
                    const newParams = {
                      ...parameters,
                      sflagaEnhancements: {
                        ...parameters.sflagaEnhancements,
                        adaptiveImmigrantRefresh: event.target.checked,
                      },
                    };
                    setParameters(newParams);
                    if (onParametersChange) {
                      onParametersChange(newParams);
                    }
                  }}
                  size="small"
                />
              }
              label={
                <Typography sx={{ fontSize: { xs: '0.8rem', md: '0.85rem' }, color: 'error.main' }}>
                  Adaptive Immigrant Refresh
                </Typography>
              }
            />
          </Tooltip>
        </Grid>
      </Grid>

      <Divider sx={{ mb: 3 }} />

      {/* Energy-Aware Optimization */}
      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, fontSize: { xs: '0.875rem', md: '1rem' } }}>
        Energy-Aware Optimization
      </Typography>
      <FormControl component="fieldset" fullWidth sx={{ mb: 2 }}>
        <FormLabel component="legend">Fitness Calculation Mode</FormLabel>
        <RadioGroup
          row
          value={parameters.fitnessMode}
          onChange={(e) => {
            const newParams = { ...parameters, fitnessMode: e.target.value as FitnessMode };
            setParameters(newParams);
            if (onParametersChange) {
              onParametersChange(newParams);
            }
          }}
        >
          {wrapWithTooltip(
            'Optimize using the QoS-only scalar fitness without the energy-aware composite weighting.',
            <FormControlLabel value="qos-only" control={<Radio size="small" />} label="QoS Only" />,
            false
          )}
          {wrapWithTooltip(
            'Optimize using the weighted energy-aware composite fitness that combines QoS and energy while still reporting cost separately.',
            <FormControlLabel value="energy-aware" control={<Radio size="small" />} label="Energy-Aware" />,
            false
          )}
        </RadioGroup>
      </FormControl>

      {parameters.fitnessMode === 'energy-aware' && (
        <>
          <Alert severity="info" sx={{ mb: 2 }}>
            Energy-aware mode optimizes for composite fitness: w_fitness × QoS + w_energy × Energy. Cost is still reported separately.
          </Alert>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              {wrapWithTooltip(
                'Weight assigned to the QoS component inside the energy-aware composite fitness.',
                <TextField
                  label="QoS Weight"
                  type="number"
                  value={parameters.energyAwareWeights.fitness}
                  onChange={handleChange('energyAwareWeights.fitness')}
                  size="small"
                  fullWidth
                  inputProps={{ min: 0, max: 1, step: 0.01 }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">w₁</InputAdornment>,
                  }}
                />
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              {wrapWithTooltip(
                'Weight assigned to the energy term inside the energy-aware composite fitness.',
                <TextField
                  label="Energy Weight"
                  type="number"
                  value={parameters.energyAwareWeights.energy}
                  onChange={handleChange('energyAwareWeights.energy')}
                  size="small"
                  fullWidth
                  inputProps={{ min: 0, max: 1, step: 0.01 }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">w₂</InputAdornment>,
                  }}
                />
              )}
            </Grid>
          </Grid>
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Weight Sum: {(parameters.energyAwareWeights.fitness + parameters.energyAwareWeights.energy).toFixed(2)}.
              Changing either value automatically updates the other to keep the total at 1.00.
            </Typography>
          </Box>
        </>
      )}

      <Divider sx={{ mb: 3 }} />

      {/* Statistical Analysis Parameters */}
      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, fontSize: { xs: '0.875rem', md: '1rem' } }}>
        Statistical Analysis
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          {wrapWithTooltip(
            'How many repeated runs to execute for the same configuration. Higher values improve statistical stability and median-based reporting.',
            <TextField
              label="Number of Runs"
              type="number"
              value={parameters.numberOfRuns}
              onChange={handleChange('numberOfRuns')}
              size="small"
              fullWidth
              inputProps={{ min: 1, max: 100 }}
              helperText="Multiple runs for statistical analysis"
            />
          )}
        </Grid>
        <Grid item xs={12} sm={6}>
          <Tooltip title="Include the standard SFLA-GA baseline as an additional comparator alongside the proposed SFLAGA variant." arrow placement="top">
            <FormControlLabel
              control={
                <Checkbox
                  checked={parameters.includeSflaGaBaseline}
                  onChange={(event) => {
                    const newParams = {
                      ...parameters,
                      includeSflaGaBaseline: event.target.checked,
                    };
                    setParameters(newParams);
                    if (onParametersChange) {
                      onParametersChange(newParams);
                    }
                  }}
                  size="small"
                />
              }
              label="SFLA-GA"
              sx={{ mt: 0.5 }}
            />
          </Tooltip>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ParameterPanel;
